"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useSession } from "next-auth/react";

interface UserData {
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface UserContextType {
  userData: UserData;
  updateUserData: (data: Partial<UserData>) => void;
  refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const [userData, setUserData] = useState<UserData>({
    name: null,
    email: null,
    phone: null,
  });

  // Load user data when session is available
  useEffect(() => {
    const loadUserData = async () => {
      if (session?.user?.id) {
        try {
          const response = await fetch("/api/user/profile");
          if (response.ok) {
            const data = await response.json();
            setUserData({
              name: data.user.name || null,
              email: data.user.email || null,
              phone: data.user.phone || null,
            });
          } else {
            // Fallback to session data
            setUserData({
              name: session.user.name || null,
              email: session.user.email || null,
              phone: null,
            });
          }
        } catch (error) {
          // Fallback to session data
          setUserData({
            name: session.user.name || null,
            email: session.user.email || null,
            phone: null,
          });
        }
      } else {
        // Clear data if not logged in
        setUserData({
          name: null,
          email: null,
          phone: null,
        });
      }
    };

    loadUserData();
  }, [session]);

  const updateUserData = (data: Partial<UserData>) => {
    setUserData((prev) => ({ ...prev, ...data }));
  };

  const refreshUserData = async () => {
    if (session?.user?.id) {
      try {
        const response = await fetch("/api/user/profile");
        if (response.ok) {
          const data = await response.json();
          setUserData({
            name: data.user.name || null,
            email: data.user.email || null,
            phone: data.user.phone || null,
          });
        }
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    }
  };

  return (
    <UserContext.Provider value={{ userData, updateUserData, refreshUserData }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

